import { IProductListItem } from 'api/types';
import { IKnowledgeBaseArticle } from 'api/endpoints/useGetKnowledgeBaseArticles';

export const generateProductPageScheme = (
  productData: IProductListItem,
  currentUrl: string
) => {
  if (!productData) return null;

  const productInfo = productData.product_basic_full_info;
  const images = productData.product_image_formatted_full_info?.images || [];
  const prices = productData.product_price_full_info?.prices || [];
  const mainPrice = prices[0];

  const imageUrls = images
    .map((img) => img.big || img.thumb)
    .filter(Boolean)
    .map((imgUrl) => {
      if (imgUrl.startsWith('http://') || imgUrl.startsWith('https://')) {
        return imgUrl;
      }
      return `${window.location.origin}${imgUrl.startsWith('/') ? '' : '/'}${imgUrl}`;
    });

  const imageObjects = imageUrls.length > 0
    ? imageUrls.map((url) => ({
        '@type': 'ImageObject',
        url: url
      }))
    : undefined;

  const logoUrl = `${window.location.origin}/assets/img/logo.png`;

  return [
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${window.location.origin}/#website`,
      url: window.location.origin,
      name: 'WiseB2B',
      publisher: {
        '@type': 'Organization',
        name: 'WiseB2B',
        logo: {
          '@type': 'ImageObject',
          url: logoUrl
        }
      }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': currentUrl,
      url: currentUrl,
      name: productInfo?.title || '',
      description: productInfo?.description || productInfo?.short_description || '',
      isPartOf: {
        '@type': 'WebSite',
        '@id': `${window.location.origin}/#website`
      }
    },
    {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': currentUrl
      },
      name: productInfo?.title || '',
      image: imageObjects || imageUrls.length > 0 ? imageUrls : undefined,
      description: productInfo?.description || productInfo?.short_description || '',
      sku: productInfo?.index || productInfo?.symbol || productData.id.toString(),
      brand: productInfo?.producer_name
        ? {
            '@type': 'Brand',
            name: productInfo.producer_name
          }
        : undefined,
      offers: mainPrice
        ? {
            '@type': 'Offer',
            url: currentUrl,
            priceCurrency: mainPrice.currency || 'PLN',
            price: mainPrice.price_gross?.toString() || mainPrice.price_gross_formatted || '',
            priceValidUntil: new Date(new Date().setFullYear(new Date().getFullYear() + 1))
              .toISOString()
              .split('T')[0],
            itemCondition: 'https://schema.org/NewCondition',
            availability: productInfo?.is_available
              ? 'https://schema.org/InStock'
              : 'https://schema.org/OutOfStock',
            seller: {
              '@type': 'Organization',
              name: 'WiseB2B',
              url: window.location.origin,
              logo: {
                '@type': 'ImageObject',
                url: logoUrl
              }
            }
          }
        : undefined
    }
  ];
};

export const generateCategoryPageScheme = (
  productsData: {
    items: IProductListItem[];
    total_count?: number;
  },
  categoryName: string,
  currentUrl: string
) => {
  if (!productsData || !productsData.items) return null;

  const items = productsData.items.slice(0, 10);

  const urlObj = new URL(currentUrl);
  const pathParts = urlObj.pathname.split('/').filter(Boolean);
  const languagePrefix = pathParts[0] ? `/${pathParts[0]}` : '';

  const logoUrl = `${window.location.origin}/assets/img/logo.png`;

  return [
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${window.location.origin}/#website`,
      url: window.location.origin,
      name: 'WiseB2B',
      publisher: {
        '@type': 'Organization',
        name: 'WiseB2B',
        logo: {
          '@type': 'ImageObject',
          url: logoUrl
        }
      }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': currentUrl,
      url: currentUrl,
      name: categoryName || '',
      description: '',
      isPartOf: {
        '@type': 'WebSite',
        '@id': `${window.location.origin}/#website`
      }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': currentUrl
      },
      name: categoryName || '',
      description: '',
      url: currentUrl,
      numberOfItems: productsData.total_count?.toString() || items.length.toString(),
      itemListElement: items.map((product, index) => {
        const productInfo = product.product_basic_full_info || product.product_basic_short_info;
        let productUrl: string;
        
        if (productInfo?.url_link) {
          const urlLink = productInfo.url_link.startsWith('/') ? productInfo.url_link : `/${productInfo.url_link}`;
          if (urlLink.startsWith('/pl/') || urlLink.startsWith('/en/')) {
            productUrl = `${window.location.origin}${urlLink}`;
          } else {
            productUrl = `${window.location.origin}${languagePrefix}${urlLink}`;
          }
        } else {
          productUrl = `${currentUrl}/${product.id}`;
        }

        return {
          '@type': 'ListItem',
          position: index + 1,
          name: productInfo?.title || '',
          url: productUrl
        };
      })
    }
  ];
};

/**
 * JSON-LD schema dla pojedynczego artykulu bloga (endpoint /cms/article/{id}).
 * Pracuje z `article_fields` typu Record<string, string|null> (NIE array — tym rozni sie od
 * generateKnowledgeBaseArticlePageScheme, ktory zaklada array z `.find()`).
 */
export const generateBlogArticlePageScheme = (
  article: {
    id: number;
    title: string;
    sys_insert_date?: string;
    sys_update_date?: string;
    article_fields?: Record<string, string | null>;
  } | null | undefined,
  currentUrl: string
) => {
  if (!article) return null;

  const fields = article.article_fields || {};
  const image = (fields['image'] as string | null) || undefined;
  const subtitleRaw =
    (fields['Podtytuł'] as string | null) ??
    (fields['podtytuł'] as string | null) ??
    (fields['subtitle'] as string | null) ??
    null;
  const contentRaw = (fields['content'] as string | null) || '';

  const stripHtml = (raw: string): string => {
    const withSpaces = raw
      .replace(/<\s*br\s*\/?\s*>/gi, ' ')
      .replace(/<\/\s*p\s*>/gi, ' ');
    const withoutTags = withSpaces.replace(/<[^>]+>/g, '');
    // Dekoduj encje HTML (&oacute;, &nbsp;, &amp; itd.) przez parser przegladarki
    let decoded = withoutTags;
    if (typeof document !== 'undefined') {
      const el = document.createElement('textarea');
      el.innerHTML = withoutTags;
      decoded = el.value;
    }
    return decoded.replace(/\s+/g, ' ').trim();
  };

  const description = subtitleRaw
    ? stripHtml(subtitleRaw).slice(0, 300)
    : contentRaw
      ? stripHtml(contentRaw).slice(0, 300)
      : '';

  const now = new Date().toISOString();
  const datePublished = article.sys_insert_date || now;
  const dateModified = article.sys_update_date || article.sys_insert_date || now;

  const origin = window.location.origin;
  const logoUrl = `${origin}/assets/img/logo.png`;
  const imageObject = image ? { '@type': 'ImageObject', url: image } : undefined;

  return [
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${origin}/#website`,
      url: origin,
      name: 'WiseB2B',
      publisher: {
        '@type': 'Organization',
        name: 'WiseB2B',
        logo: { '@type': 'ImageObject', url: logoUrl }
      }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': currentUrl,
      url: currentUrl,
      name: article.title || '',
      description,
      isPartOf: { '@type': 'WebSite', '@id': `${origin}/#website` }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      mainEntityOfPage: { '@type': 'WebPage', '@id': currentUrl },
      headline: article.title || '',
      description,
      ...(imageObject ? { image: imageObject } : {}),
      author: {
        '@type': 'Organization',
        name: 'WiseB2B',
        url: `${origin}/`,
        logo: { '@type': 'ImageObject', url: logoUrl }
      },
      publisher: {
        '@type': 'Organization',
        name: 'WiseB2B',
        logo: { '@type': 'ImageObject', url: logoUrl }
      },
      datePublished,
      dateModified
    }
  ];
};

export const generateKnowledgeBaseArticlePageScheme = (
  articleData: IKnowledgeBaseArticle,
  currentUrl: string
) => {
  if (!articleData) return null;

  const imageField = articleData.article_fields?.find(
    (field) => field.file && (field.file.translations || field.file.iterator)
  );
  
  let imageUrl: string | undefined;
  let description = '';
  let datePublished: string | undefined;
  let dateModified: string | undefined;

  if (imageField?.file) {
    const translations = imageField.file.translations || imageField.file.iterator || [];
    const currentLangTranslation = translations[0];
    
    if (currentLangTranslation) {
      imageUrl = currentLangTranslation.url;
      description = currentLangTranslation.description || '';
      datePublished = currentLangTranslation.sys_insert_date;
      dateModified = currentLangTranslation.sys_insert_date;
    }
  }

  if (!description) {
    const descriptionField = articleData.article_fields?.find(
      (field) => field.field_name === 'description' || (typeof field.value === 'string' && field.value.length > 50)
    );
    description = descriptionField && typeof descriptionField.value === 'string'
      ? descriptionField.value.replace(/<[^>]*>/g, '').substring(0, 200)
      : '';
  }

  const now = new Date().toISOString();
  datePublished = datePublished || articleData.sys_insert_date || now;
  dateModified = dateModified || articleData.sys_insert_date || now;

  const imageObject = imageUrl
    ? {
        '@type': 'ImageObject',
        url: imageUrl
      }
    : undefined;

  const logoUrl = `${window.location.origin}/assets/img/logo.png`;

  return [
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${window.location.origin}/#website`,
      url: window.location.origin,
      name: 'WiseB2B',
      publisher: {
        '@type': 'Organization',
        name: 'WiseB2B',
        logo: {
          '@type': 'ImageObject',
          url: logoUrl
        }
      }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': currentUrl,
      url: currentUrl,
      name: articleData.title || '',
      description: description,
      isPartOf: {
        '@type': 'WebSite',
        '@id': `${window.location.origin}/#website`
      }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': currentUrl
      },
      headline: articleData.title || '',
      description: description,
      image: imageObject || imageUrl,
      author: {
        '@type': 'Organization',
        name: 'WiseB2B',
        url: `${window.location.origin}/`,
        logo: {
          '@type': 'ImageObject',
          url: logoUrl
        }
      },
      publisher: {
        '@type': 'Organization',
        name: 'WiseB2B',
        logo: {
          '@type': 'ImageObject',
          url: logoUrl
        }
      },
      datePublished: datePublished,
      dateModified: dateModified,
      potentialAction: {
        '@type': 'ReadAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: currentUrl
        }
      }
    }
  ];
};


export const generateHomePageScheme = (
  seoData: {
    title: string | null;
    description: string | null;
  } | null,
  currentUrl: string
) => {
  const logoUrl = `${window.location.origin}/assets/img/logo.png`;

  return [
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${window.location.origin}/#website`,
      url: window.location.origin,
      name: seoData?.title || 'WiseB2B',
      description: seoData?.description || '',
      publisher: {
        '@type': 'Organization',
        name: 'WiseB2B',
        logo: {
          '@type': 'ImageObject',
          url: logoUrl
        }
      },
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${window.location.origin}/szukaj?q={search_term_string}`
        },
        'query-input': {
          '@type': 'PropertyValueSpecification',
          valueRequired: 'https://schema.org/True',
          valueName: 'search_term_string'
        }
      }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': currentUrl,
      url: currentUrl,
      name: seoData?.title || 'WiseB2B',
      description: seoData?.description || '',
      isPartOf: {
        '@type': 'WebSite',
        '@id': `${window.location.origin}/#website`
      }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'OnlineStore',
      '@id': `${window.location.origin}/#store`,
      name: seoData?.title || 'WiseB2B',
      url: window.location.origin,
      logo: {
        '@type': 'ImageObject',
        url: logoUrl
      },
      description: seoData?.description || '',
      image: {
        '@type': 'ImageObject',
        url: logoUrl
      }
    }
  ];
};
